import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, Observable, Subscription, tap, throwError } from 'rxjs';

import { apiErrors, apiTraceId } from '../common/ApiError';
import { errorCodeText } from '../common/error-messages';
import { applyServerFieldErrors, clearServerFieldErrors } from '../common/server-field-errors';

/** Any reactive form group, whatever its controls — `FormGroup` defaults its type parameter to `any`. */
type FormLike = FormGroup;

@Injectable({
  providedIn: 'root',
})
export class CallApiService {
  private toastr = inject(ToastrService);

  /**
   * What goes to the console: the codes, the fields and the server's own English developer
   * text, plus the trace id that matches this rejection to a log line.
   *
   * **Not** shown to the user — see showErrorToast.
   */
  private serverErrorDetails(err: unknown): string {
    const lines = apiErrors(err).map((e) =>
      [e.code, e.field ? `(${e.field})` : null, e.message, e.params ? JSON.stringify(e.params) : null]
        .filter(Boolean)
        .join(' '),
    );
    const traceId = apiTraceId(err);
    if (traceId) {
      lines.push(`traceId ${traceId}`);
    }
    return lines.join('\n');
  }

  /**
   * The user sees a localised message and never the server's own words.
   *
   * Server-supplied text used to be toasted as the body, which meant the backend's internal
   * wording reached end users verbatim — "Cannot create blob without product ID", "User
   * subject is required for update", "The Address field is required." Those are developer
   * strings: English, written against the domain model, and naming fields by their code
   * name rather than their label. A user can do nothing with them, and they expose how the
   * backend is built. That suppression stays.
   *
   * What backend #682 changes is that there is now something better than a generic toast to
   * put in its place. A code this build has translated is shown instead of the caller's
   * message, because it says what is actually wrong — „Diese E-Mail-Adresse wird bereits
   * verwendet." rather than „Beim Speichern ist ein Fehler aufgetreten." An **unknown** code
   * falls back to the caller's message exactly as before: the backend's vocabulary grows
   * independently of this repo, and a code nobody has translated yet must degrade to what the
   * user saw yesterday rather than to a blank toast or a raw camelCase identifier.
   *
   * Errors already placed at a form control are not repeated here — the message is beside the
   * field the user has to fix, and a toast saying the same thing is noise. If nothing could be
   * placed, everything still gets a toast.
   */
  private showErrorToast(errorMessage: string, err: unknown, form?: FormLike): void {
    const details = this.serverErrorDetails(err);
    if (details) {
      console.error(`${errorMessage}\n${details}`);
    }

    const remaining = form ? applyServerFieldErrors(form, apiErrors(err)) : apiErrors(err);
    const placedSomething = form && remaining.length < apiErrors(err).length;

    const coded = remaining.map((e) => errorCodeText(e)).filter((t): t is string => !!t);
    if (coded.length) {
      // One toast per distinct sentence: a request that failed three rules at once should not
      // stack three copies of the same generic line.
      for (const text of Array.from(new Set(coded))) {
        this.toastr.error(text);
      }
      return;
    }

    if (placedSomething && !remaining.length) {
      return;
    }
    this.toastr.error(errorMessage);
  }

  confirmAndCallWithToasts<T>(
    action$: Observable<T>,
    confirmMessage: string,
    successMessage: string,
    errorMessage: string,
    onSuccess?: (item: T) => void,
    onError?: (error: any) => void
  ): Subscription {
    if (confirm(confirmMessage)) {
      return this.callWithToasts(action$, successMessage, errorMessage, onSuccess, onError);
    }

    return null
  }

  /**
   * `[asyncClick]`-friendly variant of {@link confirmAndCallWithToasts}: shows the
   * confirm() prompt, then *returns* the toast-piped Observable (or EMPTY if the
   * user cancels — EMPTY completes immediately so the directive resets `busy`).
   *
   *   onDelete(id: string): () => Observable<unknown> {
   *     return () => this.api.confirmAndCallWithToasts$(
   *       this.svc.delete(id), 'Wirklich löschen?', 'Gelöscht', 'Fehler');
   *   }
   */
  confirmAndCallWithToasts$<T>(
    action$: Observable<T>,
    confirmMessage: string,
    successMessage: string,
    errorMessage: string,
    form?: FormLike
  ): Observable<T> {
    if (!confirm(confirmMessage)) {
      return EMPTY;
    }
    return this.callWithToasts$(action$, successMessage, errorMessage, form);
  }

  /**
   * Like {@link callWithToasts}, but *returns* the piped Observable instead of
   * subscribing to it. Use this from `[asyncClick]` handlers (see
   * AsyncClickDirective): the directive owns the subscription and the in-flight
   * guard, while this keeps the success/error toast strings centralised here.
   *
   *   save(): Observable<Foo> {
   *     return this.api.callWithToasts$(this.svc.update(...), 'Gespeichert', 'Fehler');
   *   }
   *
   * The error is re-thrown after the toast so the caller/directive still sees it
   * (the directive swallows it via catchError once the toast has been shown).
   */
  callWithToasts$<T>(
    action$: Observable<T>,
    successMessage: string,
    errorMessage: string,
    form?: FormLike
  ): Observable<T> {
    // #178: a server rule is a statement about the values that were just sent, so the previous
    // answer's messages come down before this one goes out — otherwise a field the user is in
    // the middle of fixing keeps yesterday's complaint.
    clearServerFieldErrors(form);

    return action$.pipe(
      tap(() => {
        if (successMessage) this.toastr.success(successMessage);
      }),
      catchError((err) => {
        if (errorMessage) this.showErrorToast(errorMessage, err, form);
        return throwError(() => err);
      })
    );
  }

  callWithToasts<T>(
    action$: Observable<T>,
    successMessage: string,
    errorMessage: string,
    onSuccess?: (item: T) => void,
    onError?: (error: any) => void,
    form?: FormLike
  ): Subscription {
    clearServerFieldErrors(form);

    return action$.subscribe({
      next: (item: T) => {
        if(successMessage) this.toastr.success(successMessage);
        if (onSuccess) {
          onSuccess(item);
        }
      },
      error: (err) => {
        if(errorMessage) this.showErrorToast(errorMessage, err, form);
        if (onError) {
          onError(err);
        }
      },
    });
  }
}
